// Client/src/partner/PartnerWorkspace.js
import React, { useCallback, useEffect, useState } from "react";
import PartnerClientMaterials from "./PartnerClientMaterials";
import {
  getPartnerWorkspaceRedirectUrl,
  hasPartnerWorkspaceSupabaseConfig,
  partnerWorkspaceSupabase,
} from "./supabaseClient";

const SUPPORT_EMAIL = "support@astoval.com";

const shellStyle = {
  minHeight: "100vh",
  padding: "clamp(14px, 3vw, 34px)",
  boxSizing: "border-box",
  background: "radial-gradient(circle at top left, #eef7f8 0%, #f5f7fb 34%, #eef1f5 100%)",
  color: "#101828",
  fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const panelStyle = {
  width: "min(100%, 1180px)",
  margin: "0 auto",
  background: "#ffffff",
  border: "1px solid #dfe5ea",
  borderRadius: 20,
  boxShadow: "0 20px 55px rgba(16, 24, 40, 0.10)",
  overflow: "hidden",
};

const buttonStyle = {
  appearance: "none",
  border: 0,
  borderRadius: 8,
  background: "#ff7a00",
  color: "#111827",
  fontWeight: 800,
  padding: "12px 16px",
  cursor: "pointer",
};

const inputStyle = {
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #d0d5dd",
  borderRadius: 8,
  padding: "11px 12px",
  fontSize: 15,
  color: "#101828",
  background: "#ffffff",
};

const labelStyle = {
  display: "block",
  marginBottom: 7,
  fontSize: 14,
  fontWeight: 750,
  color: "#344054",
};

const initialAssessmentForm = {
  caseReference: "",
  description: "",
  issuanceJurisdiction: "",
  saleJurisdictions: "",
  instrumentType: "",
  custodyModel: "",
  liquidityCadence: "",
  marketVenue: "",
  settlementModel: "not-decided",
  disclosure: "",
  transferRestrictions: "",
  taxNotes: "",
  investorProfessional: true,
  investorAccredited: false,
  investorRetail: false,
  reportLanguageCode: "en",
};

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function isPartnerStripeTestModeFromUrl() {
  if (typeof window === "undefined") return false;

  try {
    const value = new URL(window.location.href).searchParams.get("stripe_test") || "";
    return ["1", "true", "yes", "on"].includes(String(value).trim().toLowerCase());
  } catch {
    return false;
  }
}

function createWorkspaceIdempotencyKey() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return `workspace-${window.crypto.randomUUID()}`;
  }
  return `workspace-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function readableWorkspaceError(response, payload) {
  if (response.status === 401) {
    return "Your session has expired. Please request a new secure sign-in link.";
  }
  if (response.status === 403) {
    return "This account is not approved for Partner Workspace. Please contact support.";
  }
  if (payload?.message) return String(payload.message);
  return "Partner Workspace is temporarily unavailable. Please try again shortly.";
}

function readableSubmissionError(response, payload) {
  if (response.status === 401) {
    return "Your secure session has expired. Please sign in again before submitting this case.";
  }
  if (response.status === 402) {
    return "No partner report credits are currently available for this submission.";
  }
  if (response.status === 409) {
    return "A case with this case reference already exists. Use a new reference or contact support.";
  }
  if (payload?.message) return String(payload.message);
  return "The case could not be queued. Please review the information and try again.";
}


function formatAssessmentTime(value) {
  const timestamp = Date.parse(String(value || ""));
  if (!Number.isFinite(timestamp)) return "—";

  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(timestamp));
  } catch {
    return new Date(timestamp).toLocaleString();
  }
}

function isAssessmentPending(status) {
  return ["queued", "running"].includes(String(status || "").toLowerCase());
}

function assessmentStatusTone(status) {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "completed") {
    return { background: "#ecfdf3", border: "#abefc6", color: "#05603a", label: "Completed" };
  }
  if (normalized === "failed") {
    return { background: "#fef3f2", border: "#fecdca", color: "#b42318", label: "Failed" };
  }
  if (normalized === "running") {
    return { background: "#eff8ff", border: "#b2ddff", color: "#175cd3", label: "Processing" };
  }
  if (normalized === "queued") {
    return { background: "#fffaeb", border: "#fedf89", color: "#b54708", label: "Queued" };
  }

  return {
    background: "#f9fafb",
    border: "#eaecf0",
    color: "#475467",
    label: normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "Unknown",
  };
}

function readableHistoryError(response, payload) {
  if (response.status === 401) {
    return "Your secure session has expired. Please sign in again.";
  }
  if (response.status === 403) {
    return "This account is not approved to view Partner Workspace assessments.";
  }
  if (payload?.message) return String(payload.message);
  return "Assessment history is temporarily unavailable. Please try again shortly.";
}

function PartnerAssessmentHistory({ apiBase, session, refreshKey }) {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [downloadingId, setDownloadingId] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadHistory = useCallback(
    async (silent = false) => {
      if (!session?.access_token) {
        setAssessments([]);
        setLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError("");

      try {
        const response = await fetch(`${apiBase}/api/partner/workspace/assessments?limit=10`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          cache: "no-store",
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload?.ok) {
          throw new Error(readableHistoryError(response, payload));
        }

        setAssessments(Array.isArray(payload.assessments) ? payload.assessments : []);
        setLastUpdated(new Date());
      } catch (historyError) {
        setError(historyError?.message || "Assessment history could not be loaded.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [apiBase, session?.access_token]
  );

  useEffect(() => {
    void loadHistory(false);
  }, [loadHistory, refreshKey]);

  useEffect(() => {
    const hasPendingAssessment = assessments.some((assessment) =>
      isAssessmentPending(assessment?.status)
    );

    if (!hasPendingAssessment) return undefined;

    const timer = window.setInterval(() => {
      void loadHistory(true);
    }, 10000);

    return () => window.clearInterval(timer);
  }, [assessments, loadHistory]);

  async function handleDownload(assessment) {
    if (!assessment?.id || !session?.access_token) return;

    setDownloadError("");
    setDownloadingId(assessment.id);

    // Open a blank tab synchronously to avoid popup blocking. The tab only receives
    // the short-lived signed PDF URL after the authenticated Workspace request succeeds.
    const reportWindow = window.open("", "_blank");

    try {
      const response = await fetch(
        `${apiBase}/api/partner/workspace/assessments/${encodeURIComponent(assessment.id)}/report`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          cache: "no-store",
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.ok || !payload?.report?.download_url) {
        throw new Error(readableHistoryError(response, payload));
      }

      if (reportWindow) {
        try {
          reportWindow.opener = null;
        } catch {
          // Best effort only; the signed URL is short-lived and created per request.
        }
        reportWindow.location.href = payload.report.download_url;
      } else {
        window.location.assign(payload.report.download_url);
      }
    } catch (reportError) {
      if (reportWindow && !reportWindow.closed) reportWindow.close();
      setDownloadError(reportError?.message || "The report download could not be prepared.");
    } finally {
      setDownloadingId("");
    }
  }

  const hasPendingAssessment = assessments.some((assessment) =>
    isAssessmentPending(assessment?.status)
  );

  return (
    <section
      style={{
        marginTop: 24,
        padding: 20,
        borderRadius: 12,
        border: "1px solid #eaecf0",
        background: "#ffffff",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 21 }}>Recent assessments</h2>
          <p style={{ margin: "7px 0 0", color: "#475467", lineHeight: 1.5 }}>
            Your organisation’s 10 most recent TVAC partner assessments.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadHistory(true)}
          disabled={loading || refreshing}
          style={{
            ...buttonStyle,
            background: "#ffffff",
            border: "1px solid #d0d5dd",
            color: "#344054",
            opacity: loading || refreshing ? 0.6 : 1,
          }}
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {hasPendingAssessment ? (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            borderRadius: 10,
            border: "1px solid #b2ddff",
            background: "#eff8ff",
            color: "#175cd3",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          Status refreshes automatically while an assessment is queued or processing.
        </div>
      ) : null}

      {loading ? (
        <p style={{ margin: "18px 0 0", color: "#667085" }}>Loading assessments…</p>
      ) : null}

      {!loading && !error && assessments.length === 0 ? (
        <p style={{ margin: "18px 0 0", color: "#667085" }}>
          No partner assessments have been submitted yet.
        </p>
      ) : null}

      {!loading && assessments.length ? (
        <div style={{ display: "grid", gap: 12, marginTop: 18 }}>
          {assessments.map((assessment) => {
            const tone = assessmentStatusTone(assessment?.status);
            const label = assessment?.case_reference || assessment?.external_reference || "Untitled assessment";
            const completed = String(assessment?.status || "").toLowerCase() === "completed";
            const failed = String(assessment?.status || "").toLowerCase() === "failed";
            const reportAvailable = completed && Boolean(assessment?.report_available);

            return (
              <article
                key={assessment.id}
                style={{
                  padding: 15,
                  border: "1px solid #eaecf0",
                  borderRadius: 10,
                  background: "#ffffff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <strong style={{ display: "block", fontSize: 16, overflowWrap: "anywhere" }}>{label}</strong>
                    <div style={{ marginTop: 5, color: "#667085", fontSize: 13, lineHeight: 1.5 }}>
                      Submitted {formatAssessmentTime(assessment?.submitted_at)}
                      {assessment?.report_language_code
                        ? ` · ${String(assessment.report_language_code).toUpperCase()} report`
                        : ""}
                    </div>
                  </div>
                  <span
                    style={{
                      padding: "5px 9px",
                      borderRadius: 999,
                      background: tone.background,
                      border: `1px solid ${tone.border}`,
                      color: tone.color,
                      fontWeight: 800,
                      fontSize: 12,
                    }}
                  >
                    {tone.label}
                  </span>
                </div>

                {assessment?.started_at ? (
                  <div style={{ marginTop: 10, color: "#475467", fontSize: 13 }}>
                    Started {formatAssessmentTime(assessment.started_at)}
                  </div>
                ) : null}

                {assessment?.completed_at ? (
                  <div style={{ marginTop: 6, color: "#475467", fontSize: 13 }}>
                    Finished {formatAssessmentTime(assessment.completed_at)}
                  </div>
                ) : null}

                {failed ? (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 12,
                      borderRadius: 8,
                      background: "#fef3f2",
                      color: "#b42318",
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    {assessment?.failure?.message ||
                      "This assessment could not be completed. The reserved credit should have been released."}
                  </div>
                ) : null}

                {reportAvailable ? (
                  <div style={{ marginTop: 14 }}>
                    <button
                      type="button"
                      onClick={() => void handleDownload(assessment)}
                      disabled={downloadingId === assessment.id}
                      style={{ ...buttonStyle, opacity: downloadingId === assessment.id ? 0.6 : 1 }}
                    >
                      {downloadingId === assessment.id ? "Preparing PDF…" : "Download PDF"}
                    </button>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : null}

      {lastUpdated ? (
        <div style={{ marginTop: 14, color: "#98a2b3", fontSize: 12 }}>
          Last checked {formatAssessmentTime(lastUpdated)}
        </div>
      ) : null}

      {downloadError ? (
        <div role="alert" style={{ marginTop: 14, color: "#b42318", lineHeight: 1.5 }}>
          {downloadError}
        </div>
      ) : null}

      {error ? (
        <div role="alert" style={{ marginTop: 14, color: "#b42318", lineHeight: 1.5 }}>
          {error}
        </div>
      ) : null}
    </section>
  );
}

function readableCreditPurchaseError(response, payload) {
  if (response.status === 401) {
    return "Your secure session has expired. Please sign in again before purchasing credits.";
  }
  if (response.status === 403) {
    return payload?.message || "Only a Partner Workspace owner can purchase report credits.";
  }
  if (payload?.message) return String(payload.message);
  return "Credit checkout is temporarily unavailable. Please try again shortly.";
}

function CreditsCard({ credits }) {
  const available = Number(credits?.available || 0);
  const reserved = Number(credits?.reserved || 0);
  const total = Number(credits?.total_remaining || 0);

  return (
    <section
      aria-label="Partner credit balance"
      style={{
        border: "1px solid #fed7aa",
        background: "#fff7ed",
        borderRadius: 12,
        padding: 18,
        marginTop: 18,
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "#9a3412",
          fontWeight: 850,
          letterSpacing: "0.11em",
          textTransform: "uppercase",
        }}
      >
        Partner credits
      </div>
      <div style={{ marginTop: 6, fontSize: 34, lineHeight: 1, fontWeight: 850 }}>
        {available}
      </div>
      <div style={{ marginTop: 5, color: "#475467", fontSize: 14 }}>
        Available to start new assessments
      </div>
      <div
        style={{
          display: "flex",
          gap: 18,
          flexWrap: "wrap",
          marginTop: 16,
          paddingTop: 14,
          borderTop: "1px solid #fed7aa",
          fontSize: 14,
          color: "#475467",
        }}
      >
        <span><strong style={{ color: "#101828" }}>{total}</strong> total remaining</span>
        <span><strong style={{ color: "#101828" }}>{reserved}</strong> reserved</span>
      </div>
    </section>
  );
}

function PartnerCreditPurchase({ apiBase, session, isOwner, stripeTestMode = false }) {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(Boolean(isOwner));
  const [checkoutPackId, setCheckoutPackId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOwner || !session?.access_token) {
      setPacks([]);
      setLoading(false);
      setError("");
      return undefined;
    }

    let active = true;

    async function loadPacks() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(
          `${apiBase}/api/partner/workspace/credit-packs${stripeTestMode ? "?stripe_test=1" : ""}`,
          {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          cache: "no-store",
          }
        );

        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload?.ok || !Array.isArray(payload.packs)) {
          throw new Error(readableCreditPurchaseError(response, payload));
        }

        if (active) setPacks(payload.packs);
      } catch (loadError) {
        if (active) setError(loadError?.message || "Credit packs could not be loaded.");
      } finally {
        if (active) setLoading(false);
      }
    }

    void loadPacks();

    return () => {
      active = false;
    };
  }, [apiBase, isOwner, session?.access_token, stripeTestMode]);

  async function startCheckout(pack) {
    if (!session?.access_token || !pack?.id || checkoutPackId) return;

    setError("");
    setCheckoutPackId(pack.id);

    try {
      const response = await fetch(
        `${apiBase}/api/partner/workspace/credit-checkout-sessions${stripeTestMode ? "?stripe_test=1" : ""}`,
        {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ pack_id: pack.id }),
        cache: "no-store",
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.ok || !payload.checkout_url) {
        throw new Error(readableCreditPurchaseError(response, payload));
      }

      window.location.assign(payload.checkout_url);
    } catch (checkoutError) {
      setError(checkoutError?.message || "Credit checkout could not be started.");
      setCheckoutPackId("");
    }
  }

  if (!isOwner) {
    return (
      <section
        aria-label="Credit purchase access"
        style={{
          marginTop: 18,
          padding: 18,
          borderRadius: 12,
          background: "#f9fafb",
          border: "1px solid #eaecf0",
          color: "#475467",
          lineHeight: 1.5,
        }}
      >
        <strong style={{ color: "#344054" }}>Report credits</strong>
        <p style={{ margin: "7px 0 0" }}>
          Only the Partner Workspace owner can purchase additional report credits for this organisation.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label="Buy report credits"
      style={{
        marginTop: 18,
        padding: 20,
        borderRadius: 12,
        border: "1px solid #eaecf0",
        background: "#ffffff",
      }}
    >
      <h2 style={{ margin: 0, fontSize: 21 }}>
        {stripeTestMode ? "Buy test report credits" : "Buy report credits"}
      </h2>
      {stripeTestMode ? (
        <div
          role="status"
          style={{
            marginTop: 14,
            padding: 12,
            borderRadius: 10,
            border: "1px solid #b2ddff",
            background: "#eff8ff",
            color: "#175cd3",
            lineHeight: 1.5,
            fontSize: 13,
          }}
        >
          Stripe test mode is active. No real payment will be collected. Credits are allocated only after the test webhook confirms the completed test payment.
        </div>
      ) : null}
      <p style={{ margin: "7px 0 0", color: "#475467", lineHeight: 1.5, maxWidth: 680 }}>
        Credits are one-time purchases for TVAC Deep Assessments. They do not renew automatically and do not expire in the current version.
      </p>

      {loading ? (
        <p style={{ margin: "18px 0 0", color: "#667085" }}>Loading credit packs…</p>
      ) : null}

      {!loading && !error && packs.length === 0 ? (
        <p style={{ margin: "18px 0 0", color: "#667085" }}>
          Credit checkout is not configured yet. Please contact {SUPPORT_EMAIL}.
        </p>
      ) : null}

      {!loading && packs.length ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(205px, 1fr))",
            gap: 14,
            marginTop: 18,
          }}
        >
          {packs.map((pack) => {
            const buyingThisPack = checkoutPackId === pack.id;
            const isSavingPack = Boolean(pack.display_savings);

            return (
              <article
                key={pack.id}
                style={{
                  padding: 16,
                  border: isSavingPack ? "1px solid #fed7aa" : "1px solid #eaecf0",
                  borderRadius: 10,
                  background: isSavingPack ? "#fffaf2" : "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 220,
                }}
              >
                <strong style={{ fontSize: 18 }}>{pack.label}</strong>
                <div style={{ marginTop: 10, fontSize: 28, fontWeight: 850, color: "#101828" }}>
                  {pack.display_price}
                </div>
                <div style={{ marginTop: 4, color: "#475467", fontSize: 13 }}>
                  {pack.display_unit_price}
                </div>
                <div style={{ marginTop: 12, minHeight: 38, color: isSavingPack ? "#b54708" : "#667085", fontSize: 12, lineHeight: 1.45 }}>
                  {pack.display_savings || "One report credit"}
                </div>
                <button
                  type="button"
                  onClick={() => void startCheckout(pack)}
                  disabled={Boolean(checkoutPackId)}
                  style={{
                    ...buttonStyle,
                    marginTop: "auto",
                    width: "100%",
                    opacity: checkoutPackId ? 0.6 : 1,
                  }}
                >
                  {buyingThisPack ? "Opening secure checkout…" : `Buy ${pack.label}`}
                </button>
              </article>
            );
          })}
        </div>
      ) : null}

      <p style={{ margin: "16px 0 0", color: "#667085", fontSize: 12, lineHeight: 1.5 }}>
        Prices exclude VAT and any other applicable taxes.
      </p>

      {error ? (
        <div role="alert" style={{ marginTop: 14, color: "#b42318", lineHeight: 1.5 }}>
          {error}
        </div>
      ) : null}
    </section>
  );
}

function Field({ label, hint, children, required = false }) {
  return (
    <label style={{ display: "block" }}>
      <span style={labelStyle}>
        {label}{required ? <span style={{ color: "#b42318" }}> *</span> : null}
      </span>
      {children}
      {hint ? <span style={{ display: "block", marginTop: 6, color: "#667085", fontSize: 12, lineHeight: 1.45 }}>{hint}</span> : null}
    </label>
  );
}


function getWorkspaceAutomaticFooterText(partnerName) {
  const safePartnerName = String(partnerName || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
  return safePartnerName ? `${safePartnerName} · powered by TVAC` : "Powered by TVAC";
}

function normalizeWorkspaceBrandingProfile(branding) {
  const profile =
    branding?.profile && typeof branding.profile === "object" && !Array.isArray(branding.profile)
      ? branding.profile
      : {};
  const partnerName = String(profile.partner_name || "");
  const footerTextIsCustom = profile.footer_text_is_custom === true;

  return {
    partner_name: partnerName,
    logo_url: String(profile.logo_url || ""),
    primary_color: validWorkspaceHexColor(profile.primary_color) ? String(profile.primary_color) : "#ff7a00",
    accent_color: validWorkspaceHexColor(profile.accent_color) ? String(profile.accent_color) : "#ff7a00",
    footer_text: footerTextIsCustom ? String(profile.footer_text || "") : "",
    footer_text_is_custom: footerTextIsCustom,
    footer_text_default: String(profile.footer_text_default || getWorkspaceAutomaticFooterText(partnerName)),
    contact_name: String(profile.contact_name || ""),
    contact_email: String(profile.contact_email || ""),
    contact_phone: String(profile.contact_phone || ""),
    contact_url: String(profile.contact_url || ""),
    contact_url_label: String(profile.contact_url_label || ""),
    copyright_text: String(profile.copyright_text || ""),
  };
}

function validWorkspaceHexColor(value) {
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(value || "").trim());
}

function workspaceColorInputValue(value, fallback = "#ff7a00") {
  const raw = String(value || "").trim();
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw;
  const short = raw.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  return fallback;
}

function readableBrandingError(response, payload) {
  if (response.status === 401) {
    return "Your secure session has expired. Please sign in again.";
  }
  if (response.status === 403) {
    return payload?.message || "Only a Partner Workspace owner can change brand settings.";
  }
  if (response.status === 409) {
    return payload?.message || "Brand settings changed elsewhere. Refresh the page before saving again.";
  }
  if (response.status === 413) {
    return payload?.message || "Logo files must be 2 MB or smaller.";
  }
  if (payload?.message) return String(payload.message);
  return "Brand settings are temporarily unavailable. Please try again shortly.";
}

function PartnerBrandPreview({ profile }) {
  const safePrimary = validWorkspaceHexColor(profile?.primary_color) ? profile.primary_color : "#ff7a00";
  const safeAccent = validWorkspaceHexColor(profile?.accent_color) ? profile.accent_color : safePrimary;
  const partnerName = String(profile?.partner_name || "Your partner name").trim() || "Your partner name";
  const logoUrl = String(profile?.logo_url || "").trim();
  const initials = partnerName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("") || "P";

  return (
    <div
      aria-label="Co-branded report preview"
      style={{
        border: `1px solid ${safeAccent}`,
        borderRadius: 12,
        overflow: "hidden",
        background: "#ffffff",
        minHeight: 190,
      }}
    >
      <div
        style={{
          minHeight: 66,
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 16px",
          background: `linear-gradient(135deg, ${safePrimary}, ${safeAccent})`,
          color: "#ffffff",
        }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 8,
            background: "#ffffff",
            color: safePrimary,
            display: "grid",
            placeItems: "center",
            fontSize: 13,
            fontWeight: 900,
            overflow: "hidden",
            flex: "0 0 auto",
          }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={`${partnerName} logo`}
              referrerPolicy="no-referrer"
              style={{ width: "100%", height: "100%", objectFit: "contain", background: "#ffffff" }}
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : (
            initials
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 850, overflowWrap: "anywhere" }}>{partnerName}</div>
          <div style={{ marginTop: 3, fontSize: 12, opacity: 0.95 }}>powered by TVAC</div>
        </div>
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 12, letterSpacing: "0.09em", textTransform: "uppercase", fontWeight: 850, color: safePrimary }}>
          TVAC Deep Assessment Report
        </div>
        <div style={{ marginTop: 8, fontSize: 20, fontWeight: 850 }}>Co-branded report preview</div>
        <p style={{ margin: "8px 0 0", color: "#475467", fontSize: 13, lineHeight: 1.5 }}>
          New reports and client materials will use this profile while retaining TVAC methodology, attribution and standard disclaimers.
        </p>
      </div>
    </div>
  );
}

function PartnerBrandSettings({ apiBase, session }) {
  const [branding, setBranding] = useState(null);
  const [draft, setDraft] = useState(() => normalizeWorkspaceBrandingProfile(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const applyBranding = useCallback((nextBranding) => {
    setBranding(nextBranding || null);
    setDraft(normalizeWorkspaceBrandingProfile(nextBranding));

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("tvac-partner-branding-updated", { detail: nextBranding || null })
      );
    }
  }, []);

  const loadBranding = useCallback(
    async (silent = false) => {
      if (!session?.access_token) {
        setBranding(null);
        setLoading(false);
        return;
      }

      if (!silent) setLoading(true);
      setError("");

      try {
        const response = await fetch(`${apiBase}/api/partner/workspace/branding`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          cache: "no-store",
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload?.ok || !payload?.branding) {
          throw new Error(readableBrandingError(response, payload));
        }

        applyBranding(payload.branding);
      } catch (loadError) {
        setError(loadError?.message || "Brand settings could not be loaded.");
      } finally {
        setLoading(false);
      }
    },
    [apiBase, applyBranding, session?.access_token]
  );

  useEffect(() => {
    void loadBranding(false);
  }, [loadBranding]);

  function updateDraft(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
    setNotice("");
    setError("");
  }

  function updateFooterCustomization(useCustomFooter) {
    setDraft((current) => ({
      ...current,
      footer_text_is_custom: Boolean(useCustomFooter),
      footer_text: useCustomFooter ? current.footer_text : "",
    }));
    setNotice("");
    setError("");
  }

  async function handleSave(event) {
    event.preventDefault();
    setNotice("");
    setError("");

    if (!session?.access_token || !branding?.can_edit) return;

    if (!String(draft.partner_name || "").trim()) {
      setError("Partner report name is required.");
      return;
    }
    if (!validWorkspaceHexColor(draft.primary_color)) {
      setError("Primary color must be a valid 3- or 6-digit hexadecimal color.");
      return;
    }
    if (!validWorkspaceHexColor(draft.accent_color)) {
      setError("Accent color must be a valid 3- or 6-digit hexadecimal color.");
      return;
    }
    if (draft.footer_text_is_custom && !String(draft.footer_text || "").trim()) {
      setError("Enter custom footer text or turn off the custom footer setting.");
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${apiBase}/api/partner/workspace/branding`, {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          "X-Branding-Version": String(branding.branding_version || ""),
        },
        body: JSON.stringify({
          expected_branding_version: branding.branding_version,
          branding: draft,
        }),
        cache: "no-store",
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.ok || !payload?.branding) {
        throw new Error(readableBrandingError(response, payload));
      }

      applyBranding(payload.branding);
      setNotice("Brand settings saved. New reports and client materials will use this profile.");
    } catch (saveError) {
      setError(saveError?.message || "Brand settings could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoUpload() {
    setNotice("");
    setError("");

    if (!session?.access_token || !branding?.can_edit) return;
    if (!logoFile) {
      setError("Choose a PNG, JPG or WebP logo first.");
      return;
    }

    const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(String(logoFile.type || "").toLowerCase())) {
      setError("Use a PNG, JPG or WebP logo.");
      return;
    }
    if (logoFile.size > 2 * 1024 * 1024) {
      setError("Logo files must be 2 MB or smaller.");
      return;
    }

    setUploading(true);

    try {
      const response = await fetch(`${apiBase}/api/partner/workspace/branding/logo`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": logoFile.type,
          Authorization: `Bearer ${session.access_token}`,
          "X-Branding-Version": String(branding.branding_version || ""),
        },
        body: logoFile,
        cache: "no-store",
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.ok || !payload?.branding) {
        throw new Error(readableBrandingError(response, payload));
      }

      applyBranding(payload.branding);
      setLogoFile(null);
      setFileInputKey((current) => current + 1);
      setNotice("Logo uploaded. New reports and client materials will use the updated profile.");
    } catch (uploadError) {
      setError(uploadError?.message || "The logo could not be uploaded.");
    } finally {
      setUploading(false);
    }
  }

  if (loading) {
    return (
      <section
        style={{
          marginTop: 24,
          padding: 20,
          borderRadius: 12,
          border: "1px solid #eaecf0",
          background: "#ffffff",
          color: "#667085",
        }}
      >
        Loading brand settings…
      </section>
    );
  }

  if (!branding) {
    return (
      <section
        style={{
          marginTop: 24,
          padding: 20,
          borderRadius: 12,
          border: "1px solid #eaecf0",
          background: "#ffffff",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 21 }}>Brand settings</h2>
        <p style={{ margin: "8px 0 0", color: "#b42318", lineHeight: 1.5 }}>
          {error || "Brand settings are currently unavailable."}
        </p>
        <button
          type="button"
          onClick={() => void loadBranding(false)}
          style={{ ...buttonStyle, marginTop: 16, background: "#ffffff", border: "1px solid #d0d5dd", color: "#344054" }}
        >
          Try again
        </button>
      </section>
    );
  }

  const canEdit = Boolean(branding.can_edit);
  const isLocked = Boolean(branding.is_locked);
  const liveProfile = normalizeWorkspaceBrandingProfile(branding);
  const previewProfile = { ...liveProfile, ...draft };

  return (
    <section
      aria-label="Brand settings"
      style={{
        marginTop: 24,
        padding: 20,
        borderRadius: 12,
        border: "1px solid #eaecf0",
        background: "#ffffff",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 21 }}>Brand settings</h2>
          <p style={{ margin: "7px 0 0", color: "#475467", lineHeight: 1.5, maxWidth: 650 }}>
            Manage the visual profile used for new co-branded reports and client materials. TVAC methodology, attribution and standard disclaimers remain fixed.
          </p>
        </div>
        <span
          style={{
            padding: "6px 9px",
            borderRadius: 999,
            background: "#eff8ff",
            border: "1px solid #b2ddff",
            color: "#175cd3",
            fontWeight: 800,
            fontSize: 13,
          }}
        >
          Co-branded · powered by TVAC
        </span>
      </div>

      {isLocked ? (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 10,
            border: "1px solid #fedf89",
            background: "#fffaeb",
            color: "#b54708",
            lineHeight: 1.5,
            fontSize: 13,
          }}
        >
          Brand settings are currently locked for this organisation.
        </div>
      ) : null}

      {!isLocked && !canEdit ? (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 10,
            border: "1px solid #eaecf0",
            background: "#f9fafb",
            color: "#475467",
            lineHeight: 1.5,
            fontSize: 13,
          }}
        >
          You can view this profile, but only a Partner Workspace owner can change it.
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.5fr) minmax(260px, 1fr)",
          gap: 20,
          marginTop: 20,
          alignItems: "start",
        }}
      >
        <form onSubmit={handleSave}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
            <Field label="Partner report name" required hint="Displayed in the report header beside the fixed TVAC attribution.">
              <input
                value={draft.partner_name}
                onChange={(event) => updateDraft("partner_name", event.target.value)}
                maxLength={120}
                disabled={!canEdit}
                style={{ ...inputStyle, opacity: canEdit ? 1 : 0.65 }}
              />
            </Field>

            <Field label="Report contact name" hint="Optional. Shown in the co-branded report contact area.">
              <input
                value={draft.contact_name}
                onChange={(event) => updateDraft("contact_name", event.target.value)}
                maxLength={120}
                disabled={!canEdit}
                style={{ ...inputStyle, opacity: canEdit ? 1 : 0.65 }}
              />
            </Field>

            <Field label="Primary color" required hint="Use a 3- or 6-digit hexadecimal color.">
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="color"
                  value={workspaceColorInputValue(draft.primary_color)}
                  onChange={(event) => updateDraft("primary_color", event.target.value)}
                  disabled={!canEdit}
                  aria-label="Choose primary color"
                  style={{ width: 46, height: 42, padding: 3, border: "1px solid #d0d5dd", borderRadius: 8, background: "#ffffff", cursor: canEdit ? "pointer" : "default" }}
                />
                <input
                  value={draft.primary_color}
                  onChange={(event) => updateDraft("primary_color", event.target.value)}
                  placeholder="#ff7a00"
                  maxLength={7}
                  disabled={!canEdit}
                  style={{ ...inputStyle, flex: 1, opacity: canEdit ? 1 : 0.65 }}
                />
              </div>
            </Field>

            <Field label="Accent color" required hint="Used for secondary report accents and borders.">
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="color"
                  value={workspaceColorInputValue(draft.accent_color)}
                  onChange={(event) => updateDraft("accent_color", event.target.value)}
                  disabled={!canEdit}
                  aria-label="Choose accent color"
                  style={{ width: 46, height: 42, padding: 3, border: "1px solid #d0d5dd", borderRadius: 8, background: "#ffffff", cursor: canEdit ? "pointer" : "default" }}
                />
                <input
                  value={draft.accent_color}
                  onChange={(event) => updateDraft("accent_color", event.target.value)}
                  placeholder="#5fa8d3"
                  maxLength={7}
                  disabled={!canEdit}
                  style={{ ...inputStyle, flex: 1, opacity: canEdit ? 1 : 0.65 }}
                />
              </div>
            </Field>

            <Field label="Report contact email">
              <input
                type="email"
                value={draft.contact_email}
                onChange={(event) => updateDraft("contact_email", event.target.value)}
                maxLength={254}
                disabled={!canEdit}
                style={{ ...inputStyle, opacity: canEdit ? 1 : 0.65 }}
              />
            </Field>

            <Field label="Report contact phone">
              <input
                value={draft.contact_phone}
                onChange={(event) => updateDraft("contact_phone", event.target.value)}
                maxLength={64}
                disabled={!canEdit}
                style={{ ...inputStyle, opacity: canEdit ? 1 : 0.65 }}
              />
            </Field>

            <Field label="Website URL" hint="Optional. Use a full https URL.">
              <input
                type="url"
                value={draft.contact_url}
                onChange={(event) => updateDraft("contact_url", event.target.value)}
                placeholder="https://example.com"
                maxLength={500}
                disabled={!canEdit}
                style={{ ...inputStyle, opacity: canEdit ? 1 : 0.65 }}
              />
            </Field>

            <Field label="Website label" hint="Optional. For example: Visit our website.">
              <input
                value={draft.contact_url_label}
                onChange={(event) => updateDraft("contact_url_label", event.target.value)}
                maxLength={100}
                disabled={!canEdit}
                style={{ ...inputStyle, opacity: canEdit ? 1 : 0.65 }}
              />
            </Field>
          </div>

          <div style={{ marginTop: 16 }}>
            <Field
              label="Footer text"
              hint={
                draft.footer_text_is_custom
                  ? "This custom text will appear in the report sign-off area. TVAC attribution remains fixed."
                  : `Automatic for new reports: ${getWorkspaceAutomaticFooterText(draft.partner_name)}`
              }
            >
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#344054",
                  fontSize: 14,
                  cursor: canEdit ? "pointer" : "default",
                }}
              >
                <input
                  type="checkbox"
                  checked={Boolean(draft.footer_text_is_custom)}
                  onChange={(event) => updateFooterCustomization(event.target.checked)}
                  disabled={!canEdit}
                />
                Use custom footer text
              </label>

              {draft.footer_text_is_custom ? (
                <textarea
                  value={draft.footer_text}
                  onChange={(event) => updateDraft("footer_text", event.target.value)}
                  maxLength={240}
                  rows={3}
                  disabled={!canEdit}
                  style={{
                    ...inputStyle,
                    marginTop: 10,
                    resize: "vertical",
                    lineHeight: 1.45,
                    opacity: canEdit ? 1 : 0.65,
                  }}
                />
              ) : (
                <div
                  style={{
                    marginTop: 10,
                    padding: "11px 12px",
                    border: "1px solid #eaecf0",
                    borderRadius: 8,
                    background: "#f9fafb",
                    color: "#475467",
                    fontSize: 14,
                    lineHeight: 1.45,
                  }}
                >
                  {getWorkspaceAutomaticFooterText(draft.partner_name)}
                </div>
              )}
            </Field>
          </div>

          <div style={{ marginTop: 16 }}>
            <Field label="Copyright text" hint="Optional. For example: © 2026 Example Partner.">
              <input
                value={draft.copyright_text}
                onChange={(event) => updateDraft("copyright_text", event.target.value)}
                maxLength={180}
                disabled={!canEdit}
                style={{ ...inputStyle, opacity: canEdit ? 1 : 0.65 }}
              />
            </Field>
          </div>

          {canEdit ? (
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 20 }}>
              <button type="submit" disabled={saving || uploading} style={{ ...buttonStyle, opacity: saving || uploading ? 0.6 : 1 }}>
                {saving ? "Saving changes…" : "Save brand changes"}
              </button>
              <span style={{ color: "#667085", fontSize: 13 }}>
                Version {Number(branding.branding_version || 1)}
              </span>
            </div>
          ) : (
            <div style={{ marginTop: 20, color: "#667085", fontSize: 13 }}>
              Profile version {Number(branding.branding_version || 1)}
            </div>
          )}

          {notice ? <div role="status" style={{ marginTop: 16, color: "#027a48", lineHeight: 1.5 }}>{notice}</div> : null}
          {error ? <div role="alert" style={{ marginTop: 16, color: "#b42318", lineHeight: 1.5 }}>{error}</div> : null}
        </form>

        <div>
          <div style={{ color: "#344054", fontSize: 14, fontWeight: 750, marginBottom: 7 }}>Report preview</div>
          <PartnerBrandPreview profile={previewProfile} />

          <div style={{ marginTop: 18 }}>
            <div style={{ color: "#344054", fontSize: 14, fontWeight: 750, marginBottom: 7 }}>Partner logo</div>
            {previewProfile.logo_url ? (
              <div style={{ marginBottom: 10, color: "#475467", fontSize: 13, lineHeight: 1.45 }}>
                A logo is currently attached to this profile.
              </div>
            ) : (
              <div style={{ marginBottom: 10, color: "#667085", fontSize: 13, lineHeight: 1.45 }}>
                No logo uploaded yet.
              </div>
            )}

            {canEdit ? (
              <>
                <input
                  key={fileInputKey}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setLogoFile(file);
                    setNotice("");
                    setError("");
                  }}
                  style={{ display: "block", width: "100%", fontSize: 13 }}
                />
                <div style={{ marginTop: 8, color: "#667085", fontSize: 12, lineHeight: 1.45 }}>
                  PNG, JPG or WebP only · maximum 2 MB.
                  {logoFile ? ` Selected: ${logoFile.name}` : ""}
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                  <button
                    type="button"
                    onClick={() => void handleLogoUpload()}
                    disabled={!logoFile || uploading || saving}
                    style={{ ...buttonStyle, padding: "10px 13px", opacity: !logoFile || uploading || saving ? 0.6 : 1 }}
                  >
                    {uploading ? "Uploading logo…" : "Upload logo"}
                  </button>
                  {previewProfile.logo_url ? (
                    <button
                      type="button"
                      onClick={() => updateDraft("logo_url", "")}
                      disabled={uploading || saving}
                      style={{ ...buttonStyle, padding: "10px 13px", background: "#ffffff", border: "1px solid #d0d5dd", color: "#344054", opacity: uploading || saving ? 0.6 : 1 }}
                    >
                      Remove logo
                    </button>
                  ) : null}
                </div>
                {!draft.logo_url && liveProfile.logo_url ? (
                  <div style={{ marginTop: 10, color: "#b54708", fontSize: 12, lineHeight: 1.45 }}>
                    Logo removal is staged. Choose “Save brand changes” to confirm it.
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}


function PartnerAssessmentForm({ apiBase, session, credits, onSubmitted, onRefreshWorkspace }) {
  const [form, setForm] = useState(initialAssessmentForm);
  const [idempotencyKey, setIdempotencyKey] = useState(() => createWorkspaceIdempotencyKey());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const availableCredits = Number(credits?.available || 0);
  const reservedCredits = Number(credits?.reserved || 0);

  const creditStatus = availableCredits > 0
    ? {
        label: `${availableCredits} credit${availableCredits === 1 ? "" : "s"} available`,
        background: "#ecfdf3",
        color: "#027a48",
      }
    : reservedCredits > 0
      ? {
          label: `${reservedCredits} credit${reservedCredits === 1 ? " is" : "s are"} reserved for an assessment in progress`,
          background: "#eff8ff",
          color: "#175cd3",
        }
      : {
          label: "No credits currently available",
          background: "#f2f4f7",
          color: "#475467",
        };

  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function validateForm() {
    const missing = [];
    if (!form.caseReference.trim()) missing.push("case reference");
    if (form.description.trim().length < 80) missing.push("a fuller case description (at least 80 characters)");
    if (!form.issuanceJurisdiction.trim()) missing.push("issuance jurisdiction");
    if (!form.saleJurisdictions.trim()) missing.push("sale jurisdictions");
    if (!form.instrumentType) missing.push("instrument type");
    if (!form.custodyModel) missing.push("custody model");
    if (!form.liquidityCadence) missing.push("secondary-market liquidity");
    if (!form.marketVenue) missing.push("secondary-market venue");

    if (!form.investorProfessional && !form.investorAccredited && !form.investorRetail) {
      missing.push("at least one target investor type");
    }

    return missing;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setSuccess(null);

    if (!session?.access_token) {
      setError("Your secure session has expired. Please sign in again.");
      return;
    }

    if (availableCredits < 1) {
      setError("No partner report credits are currently available for this submission.");
      return;
    }

    const missing = validateForm();
    if (missing.length) {
      setError(`Please provide ${missing.join(", ")}.`);
      return;
    }

    const payload = {
      case_reference: form.caseReference.trim(),
      external_reference: form.caseReference.trim(),
      idempotency_key: idempotencyKey,
      input: {
        text: form.description.trim(),
        issuanceJurisdiction: form.issuanceJurisdiction.trim(),
        saleJurisdictions: form.saleJurisdictions
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        instrumentType: form.instrumentType,
        custodyModel: form.custodyModel,
        liquidityCadence: form.liquidityCadence,
        marketVenue: form.marketVenue,
        settlementModel: form.settlementModel,
        cashLegModel: form.settlementModel,
        paymentRails: form.settlementModel,
        disclosure: form.disclosure,
        transferRestrictions: form.transferRestrictions.trim(),
        taxNotes: form.taxNotes.trim(),
        investorTypes: {
          Professional: Boolean(form.investorProfessional),
          Accredited: Boolean(form.investorAccredited),
          Retail: Boolean(form.investorRetail),
        },
        reportLanguageCode: form.reportLanguageCode,
        deep: true,
      },
    };

    setSubmitting(true);

    try {
      const response = await fetch(`${apiBase}/api/partner/workspace/assessments`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify(payload),
        cache: "no-store",
      });

      const responsePayload = await response.json().catch(() => ({}));
      if (!response.ok || !responsePayload?.ok || !responsePayload?.assessment?.id) {
        throw new Error(readableSubmissionError(response, responsePayload));
      }

      setSuccess({ ...responsePayload.assessment, caseReference: payload.case_reference });
      setForm(initialAssessmentForm);
      setIdempotencyKey(createWorkspaceIdempotencyKey());
      await onRefreshWorkspace();
      onSubmitted?.(responsePayload.assessment);
    } catch (submissionError) {
      setError(submissionError?.message || "The case could not be queued.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      style={{
        marginTop: 24,
        padding: 20,
        borderRadius: 12,
        border: "1px solid #eaecf0",
        background: "#ffffff",
      }}
    >
      <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 21 }}>Start a new assessment</h2>
          <p style={{ margin: "7px 0 0", color: "#475467", lineHeight: 1.5, maxWidth: 660 }}>
            Submit a structured case brief for a TVAC Deep Assessment. A partner credit is reserved only when the case is accepted into the queue.
          </p>
        </div>
        <span style={{ padding: "6px 9px", borderRadius: 999, background: creditStatus.background, color: creditStatus.color, fontWeight: 800, fontSize: 13 }}>
          {creditStatus.label}
        </span>
      </div>

      {success ? (
        <div role="status" style={{ marginTop: 18, padding: 16, borderRadius: 10, border: "1px solid #abefc6", background: "#ecfdf3", color: "#05603a", lineHeight: 1.5 }}>
          <strong>Assessment queued.</strong>
          <div style={{ marginTop: 5 }}>Case reference: <strong>{success.caseReference || "Submitted case"}</strong></div>
          <div style={{ marginTop: 4 }}>Status: <strong>{success.status || "queued"}</strong></div>
          <div style={{ marginTop: 8, fontSize: 13 }}>The case has been handed to the TVAC partner worker. Its live status and PDF download will appear in Recent assessments above.</div>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} style={{ marginTop: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 16 }}>
          <Field label="Case reference" required hint="Use a clear internal reference. It must be unique within your organisation.">
            <input
              value={form.caseReference}
              onChange={(event) => updateForm("caseReference", event.target.value)}
              placeholder="e.g. ACME-PE-2026-01"
              maxLength={160}
              style={inputStyle}
            />
          </Field>

          <Field label="Report language" required>
            <select value={form.reportLanguageCode} onChange={(event) => updateForm("reportLanguageCode", event.target.value)} style={inputStyle}>
              <option value="en">English</option>
              <option value="de">German</option>
              <option value="fr">French</option>
              <option value="es">Spanish</option>
              <option value="da">Danish</option>
              <option value="zh-Hans">Simplified Chinese</option>
              <option value="pt-BR">Português (Brasil)</option>
            </select>
          </Field>

          <Field label="Issuance jurisdiction" required hint="A country or recognised jurisdiction label such as EU, EEA or UK.">
            <input
              value={form.issuanceJurisdiction}
              onChange={(event) => updateForm("issuanceJurisdiction", event.target.value)}
              placeholder="e.g. Luxembourg"
              style={inputStyle}
            />
          </Field>

          <Field label="Sale jurisdictions" required hint="Separate markets with commas.">
            <input
              value={form.saleJurisdictions}
              onChange={(event) => updateForm("saleJurisdictions", event.target.value)}
              placeholder="e.g. Germany, Netherlands"
              style={inputStyle}
            />
          </Field>

          <Field label="Instrument type" required>
            <select value={form.instrumentType} onChange={(event) => updateForm("instrumentType", event.target.value)} style={inputStyle}>
              <option value="">Select…</option>
              <option value="wrapper">Digital wrapper / mirrored record</option>
              <option value="sole-record">Sole-record / native tokenized security</option>
            </select>
          </Field>

          <Field label="Custody model" required>
            <select value={form.custodyModel} onChange={(event) => updateForm("custodyModel", event.target.value)} style={inputStyle}>
              <option value="">Select…</option>
              <option value="regulated-custodian">Regulated custodian</option>
              <option value="ta/csd">Transfer agent / CSD</option>
              <option value="self-custody">Self-custody</option>
            </select>
          </Field>

          <Field label="Secondary-market liquidity" required>
            <select value={form.liquidityCadence} onChange={(event) => updateForm("liquidityCadence", event.target.value)} style={inputStyle}>
              <option value="">Select…</option>
              <option value="none">None planned</option>
              <option value="ad-hoc otc">Ad-hoc OTC</option>
              <option value="quarterly">Quarterly windows</option>
              <option value="continuous">Continuous</option>
            </select>
          </Field>

          <Field label="Secondary-market venue" required>
            <select value={form.marketVenue} onChange={(event) => updateForm("marketVenue", event.target.value)} style={inputStyle}>
              <option value="">Select…</option>
              <option value="ATS/MTF">ATS / MTF</option>
              <option value="OTC">OTC</option>
              <option value="DEX">Permissioned DEX</option>
              <option value="CSD/TA-registered">CSD / TA-registered</option>
            </select>
          </Field>

          <Field label="Settlement and cash leg">
            <select value={form.settlementModel} onChange={(event) => updateForm("settlementModel", event.target.value)} style={inputStyle}>
              <option value="not-decided">Not decided yet</option>
              <option value="T+0 atomic">T+0 atomic settlement</option>
              <option value="T+1/2">T+1 / T+2 settlement</option>
              <option value="off-chain">Off-chain booking</option>
              <option value="stablecoin">Stablecoin or equivalent digital cash leg</option>
            </select>
          </Field>

          <Field label="Offering disclosure">
            <select value={form.disclosure} onChange={(event) => updateForm("disclosure", event.target.value)} style={inputStyle}>
              <option value="">Not decided yet</option>
              <option value="Exempt">Exempt / private placement</option>
              <option value="Prospectus">Prospectus-based offering</option>
              <option value="KID/PRIIPs">KID / PRIIPs</option>
            </select>
          </Field>
        </div>

        <div style={{ marginTop: 16 }}>
          <Field label="Case description" required hint="Describe the asset or issuer, the proposed tokenization design, the target investors, the business objective and the practical challenge. Do not include personal data or confidential client names.">
            <textarea
              value={form.description}
              onChange={(event) => updateForm("description", event.target.value)}
              placeholder="Describe the proposed tokenization case in practical terms…"
              rows={8}
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
            />
          </Field>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16, marginTop: 16 }}>
          <Field label="Transfer restrictions" hint="Optional. For example: whitelisting, lock-up periods or investor eligibility controls.">
            <textarea
              value={form.transferRestrictions}
              onChange={(event) => updateForm("transferRestrictions", event.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.45 }}
            />
          </Field>

          <Field label="Tax considerations" hint="Optional. Summarise any material known tax constraints or open questions.">
            <textarea
              value={form.taxNotes}
              onChange={(event) => updateForm("taxNotes", event.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.45 }}
            />
          </Field>
        </div>

        <fieldset style={{ margin: "18px 0 0", padding: 14, border: "1px solid #eaecf0", borderRadius: 10 }}>
          <legend style={{ padding: "0 6px", fontSize: 14, fontWeight: 750, color: "#344054" }}>Target investor types <span style={{ color: "#b42318" }}>*</span></legend>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 4 }}>
            {[
              ["investorProfessional", "Professional investors"],
              ["investorAccredited", "Accredited / qualified investors"],
              ["investorRetail", "Retail investors"],
            ].map(([key, label]) => (
              <label key={key} style={{ display: "inline-flex", alignItems: "center", gap: 7, color: "#344054", fontSize: 14 }}>
                <input type="checkbox" checked={Boolean(form[key])} onChange={(event) => updateForm(key, event.target.checked)} />
                {label}
              </label>
            ))}
          </div>
        </fieldset>

        <div style={{ marginTop: 20, padding: 14, borderRadius: 10, background: "#f9fafb", border: "1px solid #eaecf0", color: "#475467", fontSize: 13, lineHeight: 1.5 }}>
          TVAC is an informational decision-support tool. Submission does not constitute legal, regulatory, financial, investment, tax or technical advice, and you remain responsible for ensuring that the submitted case information is appropriate to share.
        </div>

        {error ? <div role="alert" style={{ marginTop: 16, color: "#b42318", lineHeight: 1.5 }}>{error}</div> : null}

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 20 }}>
          <button type="submit" disabled={submitting || availableCredits < 1} style={{ ...buttonStyle, opacity: submitting || availableCredits < 1 ? 0.55 : 1 }}>
            {submitting ? "Queueing assessment…" : "Start assessment"}
          </button>
          <span style={{ color: "#667085", fontSize: 13 }}>
            One credit will be reserved only after the case is accepted into the queue.
          </span>
        </div>
      </form>
    </section>
  );
}


function WorkspaceNavigation({ isOwner }) {
  const links = [
    { href: "#client-materials", label: "Client materials", text: "Introduce and sell TVAC" },
    { href: "#new-assessment", label: "New assessment", text: "Submit a client case" },
    { href: "#assessment-history", label: "Recent reports", text: "Track and download" },
    { href: "#brand-settings", label: "Brand settings", text: "Manage co-branding" },
  ];

  if (isOwner) {
    links.push({ href: "#buy-credits", label: "Buy credits", text: "Add report capacity" });
  }

  return (
    <nav aria-label="Partner Workspace sections" style={{ marginTop: 20 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
          gap: 10,
        }}
      >
        {links.map((link) => (
          <a
            key={link.href}
            href={link.href}
            style={{
              display: "block",
              padding: "13px 14px",
              borderRadius: 11,
              border: "1px solid #e4e7ec",
              background: "#ffffff",
              textDecoration: "none",
              color: "#101828",
              boxShadow: "0 4px 12px rgba(16, 24, 40, 0.035)",
            }}
          >
            <div style={{ fontWeight: 850, fontSize: 14 }}>{link.label}</div>
            <div style={{ marginTop: 3, color: "#667085", fontSize: 12, lineHeight: 1.35 }}>{link.text}</div>
          </a>
        ))}
      </div>
    </nav>
  );
}

function WorkspaceHero({ organizationName, memberName, role }) {
  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 17,
        padding: "clamp(24px, 4vw, 38px)",
        color: "#ffffff",
        background: "linear-gradient(125deg, #0b1720 0%, #123642 68%, #0f766e 140%)",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 330,
          height: 330,
          borderRadius: "50%",
          right: -110,
          top: -170,
          background: "#ff7a00",
          opacity: 0.18,
        }}
      />
      <div style={{ position: "relative", maxWidth: 820 }}>
        <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.13em", color: "#fdba74", fontWeight: 850 }}>
          TVAC Partner Workspace
        </div>
        <h1 style={{ margin: "12px 0 0", fontSize: "clamp(30px, 4vw, 44px)", lineHeight: 1.05, letterSpacing: "-0.025em" }}>
          {organizationName}
        </h1>
        <p style={{ margin: "14px 0 0", maxWidth: 760, color: "#dbe7ec", fontSize: 16, lineHeight: 1.6 }}>
          Present TVAC to clients, create co-branded materials, run structured pre-tokenization assessments and manage completed reports from one secure workspace.
        </p>
        <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ padding: "7px 10px", borderRadius: 999, background: "rgba(255,255,255,.11)", border: "1px solid rgba(255,255,255,.18)", fontSize: 13, fontWeight: 750 }}>
            Signed in as {memberName}
          </span>
          {role ? (
            <span style={{ padding: "7px 10px", borderRadius: 999, background: "rgba(255,122,0,.16)", border: "1px solid rgba(255,168,92,.45)", color: "#ffd6ad", fontSize: 13, fontWeight: 850, textTransform: "capitalize" }}>
              {role}
            </span>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function PartnerWorkspace({ apiBase, stripeTestMode = false }) {
  // The dedicated Partner Workspace route is served by index.js. It currently
  // does not pass the URL test flag into this component, so resolve it here as
  // well. This keeps the test-mode switch confined to the staff-restricted URL
  // and lets a newly provisioned approved test partner use Stripe Sandbox.
  const effectiveStripeTestMode = Boolean(stripeTestMode) || isPartnerStripeTestModeFromUrl();
  const [session, setSession] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [email, setEmail] = useState("");
  const [initializing, setInitializing] = useState(true);
  const [sendingLink, setSendingLink] = useState(false);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [notice, setNotice] = useState("");
  const [checkoutNotice, setCheckoutNotice] = useState("");
  const [error, setError] = useState("");
  const [assessmentHistoryRefresh, setAssessmentHistoryRefresh] = useState(0);

  async function loadWorkspace(activeSession) {
    if (!activeSession?.access_token) {
      setWorkspace(null);
      setLoadingWorkspace(false);
      return;
    }

    setLoadingWorkspace(true);
    setError("");

    try {
      const response = await fetch(`${apiBase}/api/partner/workspace/me`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${activeSession.access_token}`,
        },
        cache: "no-store",
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.ok) {
        if (response.status === 401) {
          await partnerWorkspaceSupabase.auth.signOut();
          setSession(null);
        }
        throw new Error(readableWorkspaceError(response, payload));
      }

      setWorkspace(payload);
    } catch (loadError) {
      setWorkspace(null);
      setError(loadError?.message || "Could not load Partner Workspace.");
    } finally {
      setLoadingWorkspace(false);
    }
  }

  useEffect(() => {
    if (!hasPartnerWorkspaceSupabaseConfig || !partnerWorkspaceSupabase) {
      setInitializing(false);
      return undefined;
    }

    let active = true;

    async function restoreSession() {
      const { data, error: sessionError } = await partnerWorkspaceSupabase.auth.getSession();
      if (!active) return;

      if (sessionError) {
        setError("Could not restore your secure session. Please request a new sign-in link.");
      }

      const restoredSession = data?.session || null;
      setSession(restoredSession);
      setInitializing(false);
      if (restoredSession) await loadWorkspace(restoredSession);
    }

    void restoreSession();

    const {
      data: { subscription },
    } = partnerWorkspaceSupabase.auth.onAuthStateChange((event, nextSession) => {
      if (!active) return;

      setSession(nextSession || null);
      if (event === "SIGNED_OUT") {
        setWorkspace(null);
        setLoadingWorkspace(false);
        return;
      }

      if (nextSession && (event === "INITIAL_SESSION" || event === "SIGNED_IN" || event === "TOKEN_REFRESHED")) {
        window.setTimeout(() => {
          if (active) void loadWorkspace(nextSession);
        }, 0);
      }
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session?.access_token) return undefined;

    let url;
    try {
      url = new URL(window.location.href);
    } catch {
      return undefined;
    }

    const checkoutState = url.searchParams.get("checkout");
    if (
      checkoutState !== "partner_credits_success" &&
      checkoutState !== "partner_credits_cancelled"
    ) {
      return undefined;
    }

    // Remove the transient Checkout state immediately. This avoids showing a stale
    // confirmation again if the authenticated page is refreshed later.
    url.searchParams.delete("checkout");
    url.searchParams.delete("session_id");
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);

    if (checkoutState === "partner_credits_cancelled") {
      setCheckoutNotice("Credit checkout was cancelled. No credits were added.");
      return undefined;
    }

    setCheckoutNotice(
      "Checkout completed. Your credit balance is refreshing automatically."
    );

    let active = true;
    const refreshDelays = [0, 1500, 4000, 8000, 15000];
    const timers = refreshDelays.map((delay) =>
      window.setTimeout(() => {
        if (active) void loadWorkspace(session);
      }, delay)
    );

    return () => {
      active = false;
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [session?.access_token]);

  async function handleMagicLinkRequest(event) {
    event.preventDefault();
    const normalizedEmail = String(email || "").trim().toLowerCase();

    if (!isValidEmail(normalizedEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    setError("");
    setNotice("");
    setSendingLink(true);

    try {
      const { error: signInError } = await partnerWorkspaceSupabase.auth.signInWithOtp({
        email: normalizedEmail,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: getPartnerWorkspaceRedirectUrl(),
        },
      });

      if (signInError) throw signInError;

      setNotice("If this address is approved, a secure sign-in link is on its way. Please check your inbox.");
    } catch (signInError) {
      console.warn("Partner Workspace magic-link request failed:", signInError?.message || signInError);
      setError(`We could not send a secure sign-in link. Please try again or contact ${SUPPORT_EMAIL}.`);
    } finally {
      setSendingLink(false);
    }
  }

  async function handleSignOut() {
    setError("");
    setNotice("");
    setCheckoutNotice("");
    await partnerWorkspaceSupabase.auth.signOut();
    setSession(null);
    setWorkspace(null);
  }

  if (!hasPartnerWorkspaceSupabaseConfig || !partnerWorkspaceSupabase) {
    return (
      <div style={shellStyle}>
        <div style={panelStyle}>
          <div style={{ padding: 28 }}>
            <div style={{ fontWeight: 900, letterSpacing: "0.08em", color: "#ff7a00" }}>TVAC</div>
            <h1 style={{ margin: "12px 0 8px", fontSize: 26 }}>Partner Workspace</h1>
            <p style={{ margin: 0, lineHeight: 1.55, color: "#475467" }}>
              Partner Workspace is not configured yet. Please contact{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (initializing || (session && loadingWorkspace && !workspace)) {
    return (
      <div style={shellStyle}>
        <div style={panelStyle}>
          <div style={{ padding: 28, color: "#475467" }}>Loading Partner Workspace…</div>
        </div>
      </div>
    );
  }

  if (session && workspace) {
    const organizationName = workspace.organization?.name || "Partner Workspace";
    const memberName = workspace.member?.display_name || workspace.member?.email || "Partner member";
    const memberRole = String(workspace.member?.role || "").toLowerCase();
    const isOwner = memberRole === "owner";

    return (
      <div style={shellStyle}>
        <div style={panelStyle}>
          <header
            style={{
              padding: "17px clamp(18px, 3vw, 30px)",
              borderBottom: "1px solid #eaecf0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              position: "sticky",
              top: 0,
              zIndex: 50,
              background: "rgba(255,255,255,.96)",
              backdropFilter: "blur(10px)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: "#fff7ed", border: "1px solid #fed7aa", display: "grid", placeItems: "center", fontWeight: 950, letterSpacing: "0.04em", color: "#ff7a00", fontSize: 12 }}>
                TVAC
              </div>
              <div>
                <div style={{ fontWeight: 850, color: "#101828" }}>Partner Workspace</div>
                <div style={{ marginTop: 2, fontSize: 12, color: "#667085" }}>Commercial toolkit and assessment portal</div>
              </div>
            </div>
            <button type="button" onClick={handleSignOut} style={{ ...buttonStyle, background: "#ffffff", border: "1px solid #d0d5dd", color: "#344054", padding: "10px 13px" }}>
              Sign out
            </button>
          </header>

          <main style={{ padding: "clamp(18px, 3vw, 32px)" }}>
            <WorkspaceHero
              organizationName={organizationName}
              memberName={memberName}
              role={memberRole}
            />

            <WorkspaceNavigation isOwner={isOwner} />

            <CreditsCard credits={workspace.credits} />

            {checkoutNotice ? (
              <div
                role="status"
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 10,
                  border: "1px solid #b2ddff",
                  background: "#eff8ff",
                  color: "#175cd3",
                  lineHeight: 1.5,
                }}
              >
                {checkoutNotice}
              </div>
            ) : null}

            <div id="client-materials" style={{ scrollMarginTop: 88 }}>
              <PartnerClientMaterials
                apiBase={apiBase}
                session={session}
                organizationName={organizationName}
              />
            </div>

            <div id="new-assessment" style={{ scrollMarginTop: 88 }}>
              <PartnerAssessmentForm
                apiBase={apiBase}
                session={session}
                credits={workspace.credits}
                onRefreshWorkspace={() => loadWorkspace(session)}
                onSubmitted={() => setAssessmentHistoryRefresh((current) => current + 1)}
              />
            </div>

            <div id="assessment-history" style={{ scrollMarginTop: 88 }}>
              <PartnerAssessmentHistory
                apiBase={apiBase}
                session={session}
                refreshKey={assessmentHistoryRefresh}
              />
            </div>

            <div id="brand-settings" style={{ scrollMarginTop: 88 }}>
              <PartnerBrandSettings
                apiBase={apiBase}
                session={session}
              />
            </div>

            <div id="buy-credits" style={{ scrollMarginTop: 88 }}>
              <PartnerCreditPurchase
                apiBase={apiBase}
                session={session}
                isOwner={isOwner}
                stripeTestMode={effectiveStripeTestMode}
              />
            </div>

            <section
              style={{
                marginTop: 24,
                padding: 20,
                borderRadius: 13,
                background: "#f9fafb",
                border: "1px solid #eaecf0",
              }}
            >
              <strong>Use TVAC before specialist execution begins</strong>
              <p style={{ margin: "7px 0 0", color: "#475467", lineHeight: 1.55 }}>
                The strongest partner use case is early qualification: establish whether tokenization appears capable of creating sufficient net added value, identify unresolved conditions, and then connect the client with the relevant legal, regulatory, tax, technology and market specialists only when the case justifies it.
              </p>
            </section>

            {error ? <div role="alert" style={{ marginTop: 16, color: "#b42318", lineHeight: 1.45 }}>{error}</div> : null}
          </main>
        </div>
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      <div style={panelStyle}>
        <div style={{ padding: 28 }}>
          <div style={{ fontWeight: 900, letterSpacing: "0.08em", color: "#ff7a00" }}>TVAC</div>
          <h1 style={{ margin: "12px 0 8px", fontSize: 28 }}>Partner Workspace</h1>
          <p style={{ margin: 0, color: "#475467", lineHeight: 1.55 }}>
            Sign in with a secure email link. Only approved Partner Workspace members can access this area.
          </p>

          <form onSubmit={handleMagicLinkRequest} style={{ marginTop: 22 }}>
            <label htmlFor="partner-workspace-email" style={{ display: "block", marginBottom: 7, fontSize: 14, fontWeight: 750 }}>
              Work email
            </label>
            <input
              id="partner-workspace-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@company.com"
              style={{ ...inputStyle, fontSize: 16 }}
            />
            <button type="submit" disabled={sendingLink || !email.trim()} style={{ ...buttonStyle, marginTop: 14, opacity: sendingLink || !email.trim() ? 0.6 : 1 }}>
              {sendingLink ? "Sending secure link…" : "Send secure sign-in link"}
            </button>
          </form>

          {notice ? <div role="status" style={{ marginTop: 16, color: "#027a48", lineHeight: 1.5 }}>{notice}</div> : null}
          {error ? <div role="alert" style={{ marginTop: 16, color: "#b42318", lineHeight: 1.5 }}>{error}</div> : null}

          <p style={{ margin: "22px 0 0", color: "#667085", fontSize: 13, lineHeight: 1.5 }}>
            Need help? Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PartnerWorkspace;
